import { Svg } from "components/base/svg/Svg";
import { FC, useCallback, useContext } from "react";
import {
    AuthContext,
    ChatServiceContext,
    usePoliciesPagesConfig,
    useRuntimeConfig,
} from "@finbackoffice/site-core";
import { useParams } from "next/navigation";
import Popover from "components/base/popover/Popover";
import Button from "components/base/button/Button";
import PromotionsList from "components/bonuses/promotions/list/PromotionsList";
import { useBonusBanners } from "hooks/useBonusBanners";
import { useDevice } from "hooks";
import { AssetsDirectory } from "@finbackoffice/enums";
import { SidebarContext } from "../Sidebar";
import AdvancedSidebarLanguage from "../advanced/footer/AdvancedSidebarLanguage";
import AdvancedSidebarOddsFormat from "../advanced/footer/AdvancedSidebarOddsFormat";
import styles from "./main-sidebar-footer.module.sass";
import { MainSidebarContext } from "./MainSidebar";

const MainSidebarFooter: FC = () => {
    const { lang: locale } = useParams();
    const COMMON_SITE_CONFIGS = useRuntimeConfig("COMMON_SITE_CONFIGS");
    const { enabledPoliciesConfig } = usePoliciesPagesConfig();
    const { isMobileDevice } = useDevice();
    const { languages, oddsFormats, renderPoliciesLink } = useContext(SidebarContext);
    const { scrollRef } = useContext(MainSidebarContext);
    const { showWidget, visible } = useContext(ChatServiceContext);
    const { isUserLoggedIn } = useContext(AuthContext);
    const { allBonusBanners } = useBonusBanners();

    const onChatServiceClick = useCallback(() => {
        showWidget();
    }, [showWidget]);

    return (
        <>
            <Popover
                contentClassName={styles.popoverContent}
                scrollRef={scrollRef}
                scrollBehavior="close"
                trigger={
                    <div className={styles.mainMenuItem}>
                        <div>
                            <Svg
                                src={`/${AssetsDirectory.Mobile}/${AssetsDirectory.ProfileCompact}/profile-promotions.svg`}
                                className="svg-icon"
                                prefixAppName
                            />
                            <i className={styles.arrow} />
                        </div>
                    </div>
                }>
                <nav className={styles.list}>
                    <PromotionsList
                        allBanners={allBonusBanners}
                        showText={false}
                        enableCarousel={!isMobileDevice}
                    />
                </nav>
            </Popover>
            <Popover
                contentClassName={styles.popoverContent}
                scrollRef={scrollRef}
                scrollBehavior="close"
                trigger={
                    <div className={styles.mainMenuItem}>
                        <div>
                            <Svg
                                src={`/${AssetsDirectory.Common}/${AssetsDirectory.Tablet}/${AssetsDirectory.Sidebar}/help-center.svg`}
                                className="svg-icon"
                            />
                            <i className={styles.arrow} />
                        </div>
                    </div>
                }>
                <nav className={styles.list}>{enabledPoliciesConfig.map(renderPoliciesLink)}</nav>
            </Popover>

            <div className={styles.separator} />
            <Popover
                contentClassName={styles.popoverContent}
                scrollRef={scrollRef}
                scrollBehavior="close"
                trigger={
                    <div className={styles.mainMenuItem}>
                        <div>
                            <Svg
                                src={`/${AssetsDirectory.Common}/${AssetsDirectory.Languages}/${locale}.svg`}
                                className="svg-icon"
                                prefixAppName
                            />
                            <i className={styles.arrow} />
                        </div>
                    </div>
                }>
                <nav className={styles.list}>
                    {languages.map((lang) => (
                        <AdvancedSidebarLanguage key={lang.value} language={lang} />
                    ))}
                </nav>
            </Popover>
            {COMMON_SITE_CONFIGS.contentVerticals.sport && (
                <Popover
                    contentClassName={styles.popoverContent}
                    scrollRef={scrollRef}
                    scrollBehavior="close"
                    trigger={
                        <div className={styles.mainMenuItem}>
                            <div>
                                <Svg
                                    src={`/${AssetsDirectory.Common}/${AssetsDirectory.Tablet}/${AssetsDirectory.Sidebar}/decimal.svg`}
                                    className="svg-icon"
                                />
                                <i className={styles.arrow} />
                            </div>
                        </div>
                    }>
                    <nav className={styles.list}>
                        {oddsFormats.map((format) => (
                            <AdvancedSidebarOddsFormat key={format.value} format={format} />
                        ))}
                    </nav>
                </Popover>
            )}

            {isUserLoggedIn &&
                COMMON_SITE_CONFIGS.chatService.enable &&
                !COMMON_SITE_CONFIGS.chatService.showAfterSignIn && (
                    <div className={styles.mainMenuItem}>
                        <Button
                            type="button"
                            variant="custom"
                            disabled={visible}
                            onClick={onChatServiceClick}>
                            <Svg
                                src={`/${AssetsDirectory.Mobile}/${AssetsDirectory.ProfileCompact}/profile-livechat.svg`}
                                className="svg-icon"
                                prefixAppName
                            />
                        </Button>
                    </div>
                )}
        </>
    );
};

export default MainSidebarFooter;
